import { useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FileText, Loader2, Paperclip, Send, Volume2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'
import { getConversationSource, getTicketNumber } from '@/features/complaints/conversation-meta'
import {
  complaintsApi,
  type Complaint,
  type ComplaintAction,
  type ComplaintMessage,
  type ComplaintStatus,
} from '@/services/complaints'

export const STATUS_LABELS: Record<ComplaintStatus, string> = {
  open: 'Open',
  in_review: 'In Review',
  awaiting_customer: 'Awaiting Customer',
  resolved: 'Resolved',
  closed: 'Closed',
}

const ACTION_LABELS: Record<ComplaintAction, string> = {
  open: 'Opened',
  start_review: 'Start review',
  assign: 'Assign handler',
  await_customer: 'Await customer',
  resolve: 'Resolve',
  close: 'Close',
  reopen: 'Reopen',
}

const MEDIA_ACCEPT = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.txt'

export const statusBadgeVariant = (status: ComplaintStatus) => {
  if (status === 'resolved' || status === 'closed') return 'secondary'
  if (status === 'in_review' || status === 'awaiting_customer') return 'outline'
  return 'destructive'
}

const getMessageMediaUrl = (message: ComplaintMessage) =>
  String(message.file_url ?? message.file ?? '').trim() || null

const renderAttachment = (message: ComplaintMessage) => {
  const source = getMessageMediaUrl(message)
  if (!source) {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span>{message.file_name || 'Attachment'}</span>
      </div>
    )
  }

  if (message.file_type === 'image') {
    return (
      <a href={source} target="_blank" rel="noreferrer" className="mt-2 block max-w-sm">
        <img src={source} alt={message.file_name || 'Complaint image'} className="max-h-56 rounded-lg border object-cover" />
      </a>
    )
  }

  if (message.file_type === 'video') {
    return (
      <video controls className="mt-2 max-h-56 max-w-sm rounded-lg border" src={source}>
        <track kind="captions" />
      </video>
    )
  }

  if (message.file_type === 'audio') {
    return (
      <div className="mt-2 max-w-sm rounded-lg border border-border/60 bg-muted/20 p-3">
        <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Volume2 className="h-4 w-4" />
          {message.file_name || 'Audio message'}
        </div>
        <audio controls className="w-full" src={source} />
      </div>
    )
  }

  return (
    <a
      href={source}
      target="_blank"
      rel="noreferrer"
      className="mt-2 inline-flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm hover:bg-muted/50"
    >
      <Paperclip className="h-4 w-4" />
      {message.file_name || 'Download attachment'}
    </a>
  )
}

interface ComplaintConversationProps {
  complaint: Complaint
  /** Tenant IANA timezone for timestamps; omitted keeps the existing browser-timezone output. */
  timeZone?: string | null
  /** Changes each time the complaint is picked again; clears a half-filled follow-up action. */
  selectionKey?: unknown
  /** Called after a reply, upload, or follow-up action so the owner can refresh its complaint list. */
  onChanged?: () => void
  /** Extra line under "Raised by", e.g. a link to the job or booking. */
  meta?: ReactNode
  className?: string
}

/** Complaint header, follow-up actions, message thread, and reply box. Shared by the subject panels and the complaints page. */
export function ComplaintConversation({
  complaint,
  timeZone,
  selectionKey,
  onChanged,
  meta,
  className,
}: ComplaintConversationProps) {
  const formatTimestamp = (value: string) => formatDate(value, timeZone ? { timeZone } : undefined)
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [replyBody, setReplyBody] = useState('')
  const [replyInternal, setReplyInternal] = useState(false)
  const [actionNote, setActionNote] = useState('')
  const [resolutionNote, setResolutionNote] = useState('')
  const [assignUserId, setAssignUserId] = useState('')
  const [pendingAction, setPendingAction] = useState<ComplaintAction | null>(null)
  const [lastSelectionKey, setLastSelectionKey] = useState(selectionKey)

  if (lastSelectionKey !== selectionKey) {
    setLastSelectionKey(selectionKey)
    setPendingAction(null)
    setActionNote('')
    setResolutionNote('')
    setAssignUserId('')
  }

  const messagesQuery = useQuery({
    queryKey: ['complaint-messages', complaint.id],
    queryFn: () => complaintsApi.listMessages(complaint.id),
  })

  const followUpsQuery = useQuery({
    queryKey: ['complaint-follow-ups', complaint.id],
    queryFn: () => complaintsApi.listFollowUps(complaint.id),
  })

  const invalidateConversation = () => {
    onChanged?.()
    queryClient.invalidateQueries({ queryKey: ['complaint-messages', complaint.id] })
    queryClient.invalidateQueries({ queryKey: ['complaint-follow-ups', complaint.id] })
  }

  const sendMutation = useMutation({
    mutationFn: complaintsApi.sendMessage,
    onSuccess: () => {
      setReplyBody('')
      invalidateConversation()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const uploadMutation = useMutation({
    mutationFn: complaintsApi.uploadMessage,
    onSuccess: () => {
      setReplyBody('')
      invalidateConversation()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const actionMutation = useMutation({
    mutationFn: (payload: { id: string | number; action: ComplaintAction }) =>
      complaintsApi.applyAction(payload.id, {
        action: payload.action,
        note: actionNote.trim() || undefined,
        resolution_note: payload.action === 'resolve' ? resolutionNote.trim() : undefined,
        assigned_to: payload.action === 'assign' ? assignUserId.trim() || null : undefined,
      }),
    onSuccess: (updated) => {
      toast.success(`Ticket ${getTicketNumber(updated)} updated`)
      setActionNote('')
      setResolutionNote('')
      setAssignUserId('')
      setPendingAction(null)
      invalidateConversation()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const handleAction = (action: ComplaintAction) => {
    if (action === 'resolve' && !resolutionNote.trim() && !actionNote.trim()) {
      setPendingAction(action)
      return
    }
    if (action === 'assign' && !assignUserId.trim()) {
      setPendingAction(action)
      return
    }
    actionMutation.mutate({ id: complaint.id, action })
  }

  const handleSend = () => {
    const body = replyBody.trim()
    if (!body) {
      toast.error('Enter a message')
      return
    }
    sendMutation.mutate({
      complaint_id: complaint.id,
      body,
      is_internal: replyInternal,
    })
  }

  const handleUpload = (file: File | undefined) => {
    if (!file) return
    uploadMutation.mutate({
      complaint_id: complaint.id,
      file,
      body: replyBody.trim() || undefined,
      is_internal: replyInternal,
    })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const messages = messagesQuery.data?.results ?? []
  const isSending = sendMutation.isPending || uploadMutation.isPending

  return (
    <Card className={cn('min-h-[520px]', className)}>
      <CardHeader>
        <div className="space-y-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <Badge variant="outline">{getTicketNumber(complaint)}</Badge>
                <Badge variant={statusBadgeVariant(complaint.status)}>
                  {complaint.status_label || STATUS_LABELS[complaint.status]}
                </Badge>
                <Badge variant="secondary">{getConversationSource(complaint)}</Badge>
              </div>
              <CardTitle className="text-base">{complaint.subject}</CardTitle>
              <CardDescription>
                Raised by {complaint.raised_by_name || 'Unknown'}
                {' · '}
                {complaint.complainant_role}
                {complaint.assigned_to_name ? ` · Assigned to ${complaint.assigned_to_name}` : ''}
              </CardDescription>
              {meta}
              <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                  <p className="font-medium text-foreground">Customer email</p>
                  <p className="mt-1 break-all">{complaint.customer_email || 'Not available'}</p>
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                  <p className="font-medium text-foreground">Reply address</p>
                  <p className="mt-1 break-all">{complaint.email_reply_address || 'Not configured yet'}</p>
                </div>
              </div>
            </div>
          </div>

          {(complaint.allowed_actions?.length ?? 0) > 0 ? (
            <div className="rounded-xl border border-border/70 p-3">
              <p className="mb-2 text-sm font-medium text-foreground">Follow-up actions</p>
              <div className="flex flex-wrap gap-2">
                {(complaint.allowed_actions ?? []).map((action) => (
                  <Button
                    key={action}
                    type="button"
                    size="sm"
                    variant={pendingAction === action ? 'default' : 'outline'}
                    disabled={actionMutation.isPending}
                    onClick={() => {
                      if (action === 'resolve' || action === 'assign') {
                        setPendingAction((current) => (current === action ? null : action))
                        return
                      }
                      actionMutation.mutate({ id: complaint.id, action })
                    }}
                  >
                    {ACTION_LABELS[action]}
                  </Button>
                ))}
              </div>

              {pendingAction === 'assign' ? (
                <div className="mt-3 space-y-2">
                  <Label htmlFor="assign-user-id">Assign to user ID</Label>
                  <Input
                    id="assign-user-id"
                    value={assignUserId}
                    onChange={(event) => setAssignUserId(event.target.value)}
                    placeholder="Staff user ID"
                  />
                </div>
              ) : null}

              {pendingAction === 'resolve' ? (
                <div className="mt-3 space-y-2">
                  <Label htmlFor="resolution-note">Resolution note</Label>
                  <Textarea
                    id="resolution-note"
                    rows={3}
                    value={resolutionNote}
                    onChange={(event) => setResolutionNote(event.target.value)}
                    placeholder="Explain how the ticket was resolved..."
                  />
                </div>
              ) : null}

              {pendingAction ? (
                <div className="mt-3 space-y-2">
                  <Label htmlFor="action-note">Action note (optional)</Label>
                  <Textarea
                    id="action-note"
                    rows={2}
                    value={actionNote}
                    onChange={(event) => setActionNote(event.target.value)}
                    placeholder="Add context for this follow-up..."
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPendingAction(null)}>Cancel</Button>
                    <Button
                      size="sm"
                      disabled={actionMutation.isPending}
                      onClick={() => handleAction(pendingAction)}
                    >
                      Apply {ACTION_LABELS[pendingAction]}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {complaint.resolution_note ? (
            <div className="rounded-xl border border-border/70 bg-muted/20 p-3 text-sm">
              <p className="font-medium text-foreground">Resolution</p>
              <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{complaint.resolution_note}</p>
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex h-full flex-col">
        {(followUpsQuery.data?.results?.length ?? 0) > 0 ? (
          <div className="mb-4 rounded-xl border border-border/60 bg-background p-3">
            <p className="mb-2 text-sm font-medium text-foreground">Follow-up timeline</p>
            <div className="space-y-2">
              {followUpsQuery.data?.results.map((followUp) => (
                <div key={followUp.id} className="rounded-lg border border-border/50 px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{followUp.action_label || ACTION_LABELS[followUp.action]}</span>
                    {followUp.from_status_label && followUp.to_status_label ? (
                      <span>{followUp.from_status_label} → {followUp.to_status_label}</span>
                    ) : followUp.to_status_label ? (
                      <span>{followUp.to_status_label}</span>
                    ) : null}
                    {followUp.created_at ? <span>{formatTimestamp(followUp.created_at)}</span> : null}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {followUp.performed_by_name || 'Staff'}
                    {followUp.assigned_to_name ? ` · assigned ${followUp.assigned_to_name}` : ''}
                  </p>
                  {followUp.note ? (
                    <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{followUp.note}</p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mb-4 max-h-[420px] flex-1 space-y-3 overflow-y-auto rounded-xl border border-border/60 bg-muted/10 p-4">
          {messagesQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-3/4" />
              <Skeleton className="ml-auto h-20 w-3/4" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-sm text-muted-foreground">No messages yet.</p>
          ) : (
            messages.map((message) => {
              const isStaff = message.sender_role === 'admin' || message.sender_role === 'staff'
              const isSystem = message.message_type === 'system'
              return (
                <div
                  key={message.id}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    isSystem
                      ? 'mx-auto bg-muted/50 text-center text-sm text-muted-foreground'
                      : isStaff
                        ? 'ml-auto bg-primary text-primary-foreground'
                        : 'bg-background border border-border/70'
                  }`}
                >
                  {!isSystem ? (
                    <div className={`mb-1 flex flex-wrap items-center gap-2 text-xs ${isStaff ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                      <span className="font-medium">{message.sender_name || 'User'}</span>
                      <span>{message.sender_role}</span>
                      {message.source ? <Badge variant="outline" className={isStaff ? 'border-primary-foreground/40 text-primary-foreground' : ''}>{message.source}</Badge> : null}
                      {message.is_internal ? <Badge variant="outline">Internal</Badge> : null}
                      {message.created_at ? <span>{formatTimestamp(message.created_at)}</span> : null}
                    </div>
                  ) : null}
                  {message.body ? (
                    <p className={`whitespace-pre-wrap text-sm ${
                      isSystem
                        ? 'text-muted-foreground'
                        : isStaff
                          ? 'text-primary-foreground'
                          : 'text-foreground'
                    }`}>
                      {message.body}
                    </p>
                  ) : null}
                  {message.message_type === 'attachment' ? renderAttachment(message) : null}
                </div>
              )
            })
          )}
        </div>

        <div className="space-y-3 border-t border-border/60 pt-4">
          <Textarea
            rows={3}
            value={replyBody}
            onChange={(event) => setReplyBody(event.target.value)}
            placeholder={replyInternal ? 'Write an internal note...' : 'Write an email reply...'}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={replyInternal}
                onChange={(event) => setReplyInternal(event.target.checked)}
              />
              Internal staff note
            </label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept={MEDIA_ACCEPT}
                className="hidden"
                onChange={(event) => handleUpload(event.target.files?.[0])}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isSending}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="mr-2 h-4 w-4" />
                Attach
              </Button>
              <Button size="sm" disabled={isSending} onClick={handleSend}>
                {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Supports text, images, video, audio, and documents. External replies are delivered through Postal; internal notes stay inside the portal.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
